import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Typography, Paper } from '@mui/material';
import { Description, Rule, Grading, CheckCircleOutline, ReportProblemOutlined, BlockOutlined } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import ContractsOverTime from "./ContractsOverTime";
import RulesPerTopicChart from "./RulesPerTopicChart";
import ContractsHistogram from "./ContractsHistogram";

const MetaInformationTab = () => {
    const theme = useTheme();
    const [selectedChart, setSelectedChart] = useState('contracts');
    const [metaInfo, setMetaInfo] = useState({
        contracts: [],
        rules: [],
        topics: []
    });

    useEffect(() => {
        const fetchMetaInformation = async () => {
            try {
                const response = await axios.get('/analytics/metaInformation');
                setMetaInfo(response.data);
            } catch (error) {
                console.error('Error fetching meta information:', error);
            }
        };

        fetchMetaInformation();
    }, []);

    const contracts = metaInfo.contracts || [];
    const getCachedSum = (key) => contracts.reduce((sum, contract) => sum + (contract.cached?.[key] ?? 0), 0);
    const getAverage = (value) => contracts.length ? (value / contracts.length).toFixed(1) : '0.0';

    const clauseCount = getCachedSum('clauseCount');
    const validCount = getCachedSum('validCount');
    const unfairCount = getCachedSum('unfairCount');
    const voidCount = getCachedSum('voidCount');

    const cards = [
        {
            key: 'clauses',
            title: 'Anzahl Klauseln',
            value: clauseCount,
            detail: `Avg ${getAverage(clauseCount)}`,
            icon: <Grading sx={{ backgroundColor: theme.palette.grey[200], padding: 2, borderRadius: 100 }} />
        },
        {
            key: 'valid',
            title: 'Gültige Klauseln',
            value: validCount,
            detail: `Avg ${getAverage(validCount)}`,
            icon: <CheckCircleOutline sx={{ backgroundColor: '#e8f5e9', color: theme.palette.assessment.valid, padding: 2, borderRadius: 100 }} />
        },
        {
            key: 'unfair',
            title: 'Unfaire Klauseln',
            value: unfairCount,
            detail: `Avg ${getAverage(unfairCount)}`,
            icon: <ReportProblemOutlined sx={{ backgroundColor: '#fff3e0', color: theme.palette.assessment.unfair, padding: 2, borderRadius: 100 }} />
        },
        {
            key: 'void',
            title: 'Unwirksame Klauseln',
            value: voidCount,
            detail: `Avg ${getAverage(voidCount)}`,
            icon: <BlockOutlined sx={{ backgroundColor: '#ffebee', color: theme.palette.assessment.void, padding: 2, borderRadius: 100 }} />
        },
        {
            key: 'contracts',
            title: 'Anzahl Verträge',
            value: contracts.length,
            icon: <Description sx={{ backgroundColor: theme.palette.grey[200], padding: 2, borderRadius: 100 }} />
        },
        {
            key: 'rules',
            title: 'Anzahl Prüfanleitungen',
            value: metaInfo.rules.length,
            icon: <Rule sx={{ backgroundColor: theme.palette.grey[200], padding: 2, borderRadius: 100 }} />
        }
    ];

    const renderSelectedChart = () => {
        switch (selectedChart) {
            case 'contracts':
                return <ContractsOverTime contracts={contracts} />;
            case 'clauses':
                return <ContractsHistogram contracts={contracts} metricKey="clauseCount" title="Verteilung der Klauseln pro Vertrag" xAxisTitle="Klauseln pro Vertrag" />;
            case 'valid':
                return <ContractsHistogram contracts={contracts} metricKey="validCount" title="Anteil gültiger Klauseln pro Vertrag" xAxisTitle="Anteil gültiger Klauseln" color={theme.palette.assessment.valid} valueType="percentage" />;
            case 'unfair':
                return <ContractsHistogram contracts={contracts} metricKey="unfairCount" title="Anteil unfairer Klauseln pro Vertrag" xAxisTitle="Anteil unfairer Klauseln" color={theme.palette.assessment.unfair} valueType="percentage" />;
            case 'void':
                return <ContractsHistogram contracts={contracts} metricKey="voidCount" title="Anteil unwirksamer Klauseln pro Vertrag" xAxisTitle="Anteil unwirksamer Klauseln" color={theme.palette.assessment.void} valueType="percentage" />;
            case 'rules':
                return <RulesPerTopicChart topics={metaInfo.topics} rules={metaInfo.rules} />;
            default:
                return null;
        }
    };

    return (
        <Box>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
                    gap: 3
                }}
            >
                {cards.map(card => (
                    <Paper
                        key={card.key}
                        elevation={0}
                        onClick={() => setSelectedChart(card.key)}
                        sx={{
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: 3,
                            minWidth: 0,
                            height: 'revert',
                            cursor: 'pointer',
                            border: selectedChart === card.key ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                            '&:hover': {
                                bgcolor: theme.palette.grey[50]
                            }
                        }}
                    >
                        {card.icon}
                        <Box>
                            <Typography variant="body1" fontWeight="bold" sx={{ color: theme.palette.grey[400] }}>{card.title}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{card.value}</Typography>
                                {card.detail && (
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>{card.detail}</Typography>
                                )}
                            </Box>
                        </Box>
                    </Paper>
                ))}
            </Box>
            <Box sx={{mt: '2em'}}>
                {renderSelectedChart()}
            </Box>
        </Box>
    );
};

export default MetaInformationTab;
